import React, { useRef, useState } from 'react';
import { SEND_EMAIL_OTP, VERIFY_EMAIL_OTP } from '../../graphql/mutation';
import { useMutation } from '@apollo/client';
import Email from '../common/email';
import { FingerPrintHash } from '../../../utils/extension';
import { Box, Button, Divider, FormHelperText, Stack, Typography } from '@mui/material';
import Toast from '../common/toast';
import LocalStorage from 'utils/extension/localstorage';
import { ChevronLeft } from 'Components/Icons/ChevronLeft';
import { VERIFY_EMAIL } from 'schemas/mutation';

const otpLength = 4;
const OTP = ({ setActiveStep = () => {}, email = '', goBack = true, isConsentOtp = false, setLoading = () => {} }) => {
    const [verifyEmail] = useMutation(VERIFY_EMAIL);
    const token = LocalStorage.getAuthToken();
    const [verifyOTPData] = useMutation(VERIFY_EMAIL_OTP, {
        context: {
            extToken: token
        }
    });
    const [sendEmailOtp] = useMutation(SEND_EMAIL_OTP, {
        context: {
            extToken: token
        }
    });
    const inputRefs = useRef([]);
    const [disableButton, setDisableButton] = useState(true);
    const [error, setError] = useState(null);
    const [toastMessage, setToastMessage] = useState(null);

    const handleInput = (e, index) => {
        const value = e.target.value;
        if (value.length === 1 && index < inputRefs.current.length - 1) {
            inputRefs.current[index + 1].focus();
        }

        const inputs = inputRefs.current;
        const otpCodes = inputs.map((item) => item.value);
        const otpCodesLength = otpCodes.join('').trim()?.length;
        if (otpCodesLength === otpLength) {
            setDisableButton(false);
        }

        if (otpCodesLength < otpLength) {
            setDisableButton(true);
        }
    };

    const handleKeyDown = (e, index) => {
        if (e.key === 'Backspace' && !e.target.value && index > 0) {
            inputRefs.current[index - 1].focus();
        }
    };

    const onClickContinue = async () => {
        const inputs = inputRefs.current;
        const otpCodes = inputs.map((item) => item.value).join('');
        const fingerHash = await FingerPrintHash();
        try {
            setLoading(true);
            const { data } = await verifyOTPData({
                variables: {
                    verifyEmailOtpInput: {
                        email,
                        fingerprint: fingerHash,
                        otpCode: parseInt(otpCodes),
                        firebaseToken: null,
                        isConsentOtp
                    }
                }
            });

            if (data && data.verifyEmailOtp.userData) {
                const { token } = data.verifyEmailOtp.userData;
                LocalStorage.setAuthToken(token?.accessToken);
                setLoading(false);
                setActiveStep(2);
            }
        } catch (error) {
            setError(error.message);
            setLoading(false);
        }
    };

    const onResendOtp = async () => {
        try {
            setLoading(true);

            const response = await verifyEmail({ variables: { body: { email, isResendOtp: true } } });
            setLoading(false);
            setToastMessage(response.data.verifyExtensionEmail.message ?? '');
        } catch (error) {
            setLoading(false);
        }
    };

    return (
        <>
            <Box gap={3} display="flex" flexDirection="column" alignItems="flex-start" justifyContent="flex-start">
                {goBack ? (
                    <Stack direction="row" onClick={() => setActiveStep(0)} display="flex" alignItems="center">
                        <ChevronLeft />
                        <Typography className="color-light-yellow font-roboto fw-500">Sign out</Typography>
                    </Stack>
                ) : (
                    <Divider />
                )}
                <Stack direction="column" gap={2}>
                    <Typography className="text-dark-blue font-roboto fw-500 font-base">
                        Enter the verification code we have sent you via email.
                    </Typography>
                    <Email email={email} />
                    {isConsentOtp && (
                        <Typography className="text-dark-blue font-roboto font-normal">
                            To ensure your account's security, we need to verify your identity. This step also allows us to securely add
                            your email to our log and obtain your consent for our extension to access your email information.
                        </Typography>
                    )}
                </Stack>
                <Box
                    flex={1}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-evenly"
                    flexBasis={0}
                    flexWrap="wrap"
                    width="100%"
                    gap={2}
                >
                    {Array.from({ length: otpLength }, (_, i) => i + 1).map((item, index) => (
                        <input
                            key={item}
                            inputMode="numeric"
                            ref={(el) => (inputRefs.current[index] = el)}
                            style={{
                                width: '40px',
                                flexGrow: 1,
                                height: '48px',
                                margin: '0 8px',
                                textAlign: 'center',
                                backgroundColor: '#fafafa',
                                border: '1px solid #fafafa',
                                borderRadius: '4px',
                                fontSize: '18px'
                            }}
                            maxLength={1}
                            onInput={(e) => handleInput(e, index)}
                            onKeyDown={(e) => handleKeyDown(e, index)}
                        />
                    ))}
                </Box>
                {error && (
                    <FormHelperText error id="standard-weight-helper-text-email-login">
                        {error}
                    </FormHelperText>
                )}
                <Button
                    variant="outlined"
                    fullWidth
                    disableElevation
                    disableRipple
                    size="large"
                    className={disableButton ? 'alternate-disabled' : 'alternate'}
                    onClick={onClickContinue}
                    disabled={disableButton}
                >
                    Continue
                </Button>
                <Stack width="100%" gap={2}>
                    <Typography className="font-light fw-600 font-roboto">Did not receive the email? Check your junk mail, or</Typography>
                    <Button
                        disableElevation
                        disableRipple
                        fullWidth
                        size="large"
                        type="submit"
                        variant="contained"
                        className="alternate-reversed"
                        onClick={onResendOtp}
                        marginTop={24}
                    >
                        Resend code
                    </Button>
                </Stack>
            </Box>
            {!!toastMessage && <Toast message={toastMessage} />}
        </>
    );
};

export default OTP;
